// =============================================================================
// StaticSitesPage — /strony: lista zmigrowanych domen (static_sites) z wejsciem
// do Studio per domena (/strony/:websiteId). Migracje zaklada sie w mapie.
// =============================================================================

import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { TopBar } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Layers, ExternalLink, Loader2, ArrowRight } from "lucide-react";
import { useStaticSites } from "@/hooks/useStaticSites";
import { StaticSiteStatusBadge, engineLabel } from "@/components/static-sites/statusBadge";

const StaticSitesPage = () => {
  const { items, loading, error } = useStaticSites();
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const rows = useMemo(() => {
    const f = q.trim().toLowerCase();
    return f ? items.filter((i) => i.site.domain.includes(f) || (i.mapName || "").toLowerCase().includes(f)) : items;
  }, [items, q]);

  return (
    <>
      <TopBar title="Strony statyczne" />
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-6xl space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div>
              <h2 className="flex items-center gap-2 text-base font-semibold"><Layers className="h-4 w-4" /> Strony przeniesione z WordPressa</h2>
              <p className="text-sm text-muted-foreground">Każda domena ma swoje Studio: motyw, design, podgląd, zgodność i deploy. Nową migrację zakładasz w mapie (krok Migracja).</p>
            </div>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Szukaj domeny…" className="ml-auto h-9 w-56" />
          </div>

          {error && <Card className="border-destructive/40"><CardContent className="py-3 text-sm text-destructive">{error}</CardContent></Card>}

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Wczytuję…</div>
          ) : rows.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                {items.length === 0 ? (
                  <>Jeszcze nic nie przeniesiono. Otwórz mapę z przypisaną domeną i kliknij „Załóż migrację” w kroku Migracja. <Link to="/mapy-tematyczne" className="underline">Mapy tematyczne</Link></>
                ) : "Brak wyników."}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Domena</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Silnik · motyw</TableHead>
                      <TableHead>Adres testowy</TableHead>
                      <TableHead>Mapa</TableHead>
                      <TableHead>Zmieniono</TableHead>
                      <TableHead className="w-32" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map(({ site, mapName }) => (
                      <TableRow key={site.id} className="cursor-pointer" onClick={() => navigate(`/strony/${site.website_id}`)}>
                        <TableCell className="font-mono text-sm">{site.domain}</TableCell>
                        <TableCell><StaticSiteStatusBadge status={site.status} /></TableCell>
                        <TableCell className="text-xs text-muted-foreground">{engineLabel(site)}</TableCell>
                        <TableCell className="text-xs">
                          {site.pages_subdomain ? (
                            <a href={`https://${site.pages_subdomain}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 underline">{site.pages_subdomain} <ExternalLink className="h-3 w-3" /></a>
                          ) : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-xs">
                          <Link to={`/mapy-tematyczne/${site.map_id}`} onClick={(e) => e.stopPropagation()} className="underline">{mapName || "mapa"}</Link>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{new Date(site.updated_at).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" })}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); navigate(`/strony/${site.website_id}`); }}>Otwórz studio <ArrowRight className="ml-1 h-3.5 w-3.5" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  );
};

export default StaticSitesPage;
